// SPDX-License-Identifier: Apache-2.0

// active-link.tsx — animated Bezier tube + particle stream from the active
// doc node to the brain core.
//
// Port of v1 vault-orbit.tsx Tendril (~990-1106). v1 ran the tube rebuild on
// useFrame; here we expose the same logic as a class with `.update(elapsed)`
// the imperative outer loop calls.
//
// PARTICLE_COUNT = 100 per the brief (v1 used 28; brief asks for "100
// particles flowing along it"). Tube radius and rebuild cadence (0.05s) match
// v1 exactly so the visual pulse looks the same.
//
// The .tsx extension is leftover from the brief's file plan — this module is
// pure imperative three.js, no JSX. (Solid still builds .tsx fine.)

import * as THREE from "three";
import { TINT_HEX } from "./nodes";
import type { VaultDoc } from "@tether/storage";

const PARTICLE_COUNT = 100;
const TUBE_REBUILD_INTERVAL_SEC = 0.05;

export interface ActiveLink {
  group: THREE.Group;
  setActive(id: string | null, doc: VaultDoc | null): void;
  /** Per-frame: feed the active doc's world position. The brain core lives at origin. */
  update(elapsedSec: number, activeWorldPos: THREE.Vector3 | null): void;
  dispose(): void;
}

export function createActiveLink(): ActiveLink {
  const group = new THREE.Group();

  // ── Tube (geometry rebuilt every ~50ms; reuse a placeholder geom up front) ──
  const seedCurve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0.5, 0),
    new THREE.Vector3(1, 0, 0),
  );
  let tubeGeom: THREE.TubeGeometry = new THREE.TubeGeometry(seedCurve, 24, 0.028, 8, false);
  const tubeMat = new THREE.MeshBasicMaterial({
    color: 0x67e8f9,
    transparent: true,
    opacity: 0,
    toneMapped: false,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const tube = new THREE.Mesh(tubeGeom, tubeMat);
  tube.frustumCulled = false;
  tube.raycast = () => undefined;
  group.add(tube);

  // ── Particles ──────────────────────────────────────────────────────────────
  const particlePositions = new Float32Array(PARTICLE_COUNT * 3);
  const particleOffsets = new Float32Array(PARTICLE_COUNT);
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particleOffsets[i] = Math.random();
  }
  const particleGeom = new THREE.BufferGeometry();
  particleGeom.setAttribute("position", new THREE.BufferAttribute(particlePositions, 3));
  const particleMat = new THREE.PointsMaterial({
    size: 0.085,
    color: 0xe0f7ff,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
    toneMapped: false,
  });
  const particles = new THREE.Points(particleGeom, particleMat);
  particles.frustumCulled = false;
  particles.raycast = () => undefined;
  group.add(particles);

  // Reused scratch
  const curve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(),
    new THREE.Vector3(),
    new THREE.Vector3(),
  );
  const tmpFrom = new THREE.Vector3(0, 0, 0); // brain core lives at origin
  const tmpMid = new THREE.Vector3();
  const tmpLift = new THREE.Vector3(0, 0.5, 0);
  const tmpColor = new THREE.Color();

  let activeId: string | null = null;
  let lastRebuild = 0;

  return {
    group,
    setActive(id: string | null, doc: VaultDoc | null) {
      activeId = id;
      if (doc) {
        const hex = TINT_HEX[doc.tint] ?? "#67e8f9";
        tmpColor.setStyle(hex);
        tubeMat.color.copy(tmpColor);
        // Particle color is a brightened blend of tint toward white
        tmpColor.lerp(new THREE.Color(0xffffff), 0.6);
        particleMat.color.copy(tmpColor);
      } else {
        tubeMat.color.set(0x67e8f9);
        particleMat.color.set(0xe0f7ff);
      }
    },
    update(elapsedSec: number, activeWorldPos: THREE.Vector3 | null) {
      const t = elapsedSec;

      if (!activeId || !activeWorldPos) {
        // Fade out
        tubeMat.opacity += (0 - tubeMat.opacity) * 0.1;
        particleMat.opacity += (0 - particleMat.opacity) * 0.1;
        return;
      }

      // Build curve: brain core (origin) → midpoint lifted up → active node
      tmpMid.copy(tmpFrom).add(activeWorldPos).multiplyScalar(0.5).add(tmpLift);
      curve.v0.copy(tmpFrom);
      curve.v1.copy(tmpMid);
      curve.v2.copy(activeWorldPos);

      // Rebuild tube ~20Hz so it isn't a per-frame allocation storm.
      if (t - lastRebuild > TUBE_REBUILD_INTERVAL_SEC) {
        const next = new THREE.TubeGeometry(curve, 24, 0.028, 8, false);
        const prev = tube.geometry;
        tube.geometry = next;
        tubeGeom = next;
        prev.dispose();
        lastRebuild = t;
      }

      // Pulsing opacity (heart-beat at ~1Hz)
      const beat = 1 + Math.sin(t * 6) * 0.12;
      tubeMat.opacity += (0.55 * beat - tubeMat.opacity) * 0.2;

      // Stream particles along the curve
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const p = (particleOffsets[i]! + t * 0.55) % 1;
        const pt = curve.getPoint(p);
        particlePositions[i * 3 + 0] = pt.x;
        particlePositions[i * 3 + 1] = pt.y;
        particlePositions[i * 3 + 2] = pt.z;
      }
      const attr = particleGeom.getAttribute("position") as THREE.BufferAttribute;
      attr.needsUpdate = true;
      particleMat.opacity += (1 - particleMat.opacity) * 0.12;
    },
    dispose() {
      tubeGeom.dispose();
      tubeMat.dispose();
      particleGeom.dispose();
      particleMat.dispose();
    },
  };
}
